import {
  decodeReadingDraft,
  READING_DRAFT_HASH_KEY,
  validateReadingDraft,
  type ReadingDraft,
} from "../../../../shared-pricing/readingDraft";
import { SHARED_SPREADS } from "../../../../shared-pricing/spreads";

export type { ReadingDraft } from "../../../../shared-pricing/readingDraft";

/**
 * The landing's reading draft, kept in localStorage (not sessionStorage) so it
 * survives sign-up when the e-mail confirmation opens in a new tab.
 */
const STORAGE_KEY = "pending_reading_draft";
export const READING_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const READING_DRAFT_PATH = "/draft";

type StoredDraft = { draft: ReadingDraft; savedAt: number };

function readStored(now: number): ReadingDraft | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  const parsed = JSON.parse(raw) as Partial<StoredDraft>;
  const draft = validateReadingDraft(parsed.draft);
  const savedAt = typeof parsed.savedAt === "number" ? parsed.savedAt : 0;
  if (!draft || now - savedAt > READING_DRAFT_TTL_MS) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return draft;
}

export function storeReadingDraft(draft: ReadingDraft, now = Date.now()): void {
  try {
    const value: StoredDraft = { draft, savedAt: now };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked: the visitor can still build the reading in the app.
  }
}

export function peekReadingDraft(now = Date.now()): ReadingDraft | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return readStored(now);
  } catch {
    // Unparsable entry: drop it so it isn't re-read on every navigation.
    clearReadingDraft();
    return null;
  }
}

export function clearReadingDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Saves `#draft=…` from the current URL and strips it, before Kinde login
 * redirects away. Returns true when a valid draft was captured.
 */
export function captureReadingDraftFromLocation(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const { pathname, search, hash } = window.location;
  if (!hash) {
    return false;
  }

  const params = new URLSearchParams(hash.slice(1));
  const encoded = params.get(READING_DRAFT_HASH_KEY);
  if (encoded === null) {
    return false;
  }

  const draft = decodeReadingDraft(encoded);
  // An invalid new draft must not resurrect an older stored one.
  if (draft) {
    storeReadingDraft(draft);
  } else {
    clearReadingDraft();
  }

  params.delete(READING_DRAFT_HASH_KEY);
  const nextHash = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${pathname}${search}${nextHash ? `#${nextHash}` : ""}`
  );
  return draft !== null;
}

export function readingDraftSpreadName(draft: ReadingDraft): string {
  return SHARED_SPREADS[draft.s].appName;
}
